// ============================================================
// VCS.AudioFader -- Switch zwischen Original-Stimme und KI-Uebersetzung
// ============================================================
// Frueher: vertikaler Crossfade-Slider mit Mix-Pfad ueber Web-Audio.
// Jetzt: harter Toggle-Switch -- Original ODER Uebersetzung, kein Mix.
//
// Hintergrund der Umstellung 2026-05-10:
//   Im Crossfade-Modus lief der Original-Stream parallel ueber das
//   <video>-Element UND ueber MediaStreamAudioSourceNode -> originalGain
//   -> destination. Selbst bei pos=0 (Original) waren beide Pfade aktiv,
//   der Latenz-Versatz erzeugte ein leichtes Echo. Der Switch loest das
//   architektonisch: Original kommt AUSSCHLIESSLICH aus dem <video>-
//   Element, Uebersetzung AUSSCHLIESSLICH aus dem Web-Audio-Graph.
//
// ARCHITEKTUR:
//   - Mode 'original' (Default): <video>.muted = false, translatedGain = 0.
//     Original-Stimme kommt direkt aus dem WebRTC-<video>-Element.
//     KEIN Web-Audio im Original-Pfad -- garantiert kein Echo.
//   - Mode 'translated': <video>.muted = true, translatedGain = 1.
//     Nur die KI-Uebersetzungs-Stimme aus dem Web-Audio-Graph laeuft.
//   - Speaker-Mute (call-controls.js): <video>.muted = true UND
//     translatedGain = 0 -- beide Pfade erfasst.
//
// BANDBREITE (2026-08-02):
//   Das TTS-Audio sind ~500 kbit/s pro Richtung. Frueher schickte der Server
//   es an jeden sprachlich passenden Empfaenger, auch wenn dessen Toggle auf
//   'Original' stand -- der Client hat es dann mit Gain 0 verworfen. Jetzt
//   meldet _sendAudioPref() den Wunsch per 'caption-audio-pref', und der
//   Server sendet NUR nach ausdruecklichem Ja. Bleibt die Meldung aus, bleibt
//   die Uebersetzung stumm -- darum setzt _sendAudioPref() seinen Cache erst,
//   wenn die Nachricht wirklich rausging, und onEngineInfo() meldet erneut.
//
// SICHTBARKEIT (alle Bedingungen muessen erfuellt sein):
//   1. Plan bringt ein Live-Kontingent mit (chatPlanLiveQuota, vom Server)
//   2. captionEngine === 'openai-realtime' (vom Backend gepushed)
//   3. captionAudioAvailable === true       (vom Backend gepushed)
//   4. in 1:1-Call (Group-Call: Iter 2 mit per-Sprecher-Lang)
//   5. peerLang gesetzt und ungleich currentLang
//
// Modul-Name VCS.audioFader bleibt aus Kompatibilitaets-Gruenden zu den
// 5 Aufrufstellen (webrtc.js, captions.js, signaling.js, call-controls.js,
// app.js). HTML/CSS heissen jetzt audioToggle / .call-audio-toggle*.
// ============================================================
window.VCS = window.VCS || {};

// noinspection JSUnresolvedReference
VCS.AudioFader = class {

    constructor() {
        // Wiedergabe-Scheduling fuer uebersetzte Audio-Chunks
        this._nextStartAt = 0;
        this._maxQueueAheadSec = 1.5;

        // Zuletzt an den Server gemeldeter Audio-Wunsch (null = noch nichts
        // gemeldet). Verhindert, dass jeder _applyMode-Aufruf eine Nachricht
        // ausloest -- siehe _sendAudioPref().
        this._lastAudioPrefSent = null;

        // View-Cache (gefuellt in init())
        this._view = null;
        this._onboardingTimer = null;

        // Sichtbarkeits-Cache (vermeidet DOM-Thrash bei schnellen
        // State-Wechseln)
        this._lastVisible = false;
        // Diagnose: Reason der letzten Sichtbarkeits-Pruefung
        this._lastEligibleReason = undefined;
    }

    // -------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------

    init() {
        this._view = {
            root      : document.getElementById('audioToggle'),
            track     : document.querySelector('#audioToggle .call-audio-toggle__track'),
            thumb     : document.querySelector('#audioToggle .call-audio-toggle__thumb'),
            labelOrig : document.querySelector('#audioToggle .call-audio-toggle__label--original'),
            labelTrans: document.querySelector('#audioToggle .call-audio-toggle__label--translated'),
            onboarding: document.getElementById('audioToggleOnboarding'),
        };
        if (!this._view.root) {
            console.warn('[audio-toggle] #audioToggle fehlt -- UI deaktiviert');
            return;
        }

        this._bindClick();
        this._bindKeyboard();

        // Onboarding-Tooltip schliesst bei Klick
        if (this._view.onboarding) {
            this._view.onboarding.addEventListener('click', () => this._dismissOnboarding(true));
        }

        // Default: 'original'
        this._renderMode('original');
        this.updateVisibility();
    }

    // -------------------------------------------------------------
    // Oeffentliches API (aufgerufen aus webrtc.js, captions.js,
    // signaling.js, call-controls.js)
    // -------------------------------------------------------------

    onCallStart() {
        VCS.state.audioFaderMode = 'original';
        this._renderMode('original');
        this._dismissOnboarding(/*persist*/ false);
        // Neuer Call = neue Client-ID auf dem Server, der Wunsch ist dort
        // unbekannt. Cache leeren, damit die Meldung sicher rausgeht --
        // ohne sie sendet der Server das TTS-Audio per Default.
        this._lastAudioPrefSent = null;
        this._sendAudioPref(false);
        this.updateVisibility();
    }

    // Aufruf aus call-controls.js nach Speaker-Toggle.
    // Setzt translatedGain und <video>.muted gemaess speakerOff-Flag.
    onSpeakerToggle() {
        this._applyMode(VCS.state.audioFaderMode || 'original');
    }

    // Toggle umschalten (Klick/Tastatur)
    toggle() {
        const cur = VCS.state.audioFaderMode || 'original';
        const next = (cur === 'original') ? 'translated' : 'original';
        this.setMode(next);
        this._dismissOnboarding(/*persist*/ true);
    }

    setMode(mode) {
        const m = (mode === 'translated') ? 'translated' : 'original';
        VCS.state.audioFaderMode = m;
        // Beim Wechsel auf 'translated' den AudioContext sicherstellen,
        // damit der erste eingehende Chunk nicht verloren geht. handleChunk
        // baut den Context zwar lazy auf, aber UI-Wechsel sollte den Gain
        // schon bereitstellen.
        if (m === 'translated') this._ensureContext();
        this._applyMode(m);
        this._renderMode(m);
    }

    // 5 Sichtbarkeits-Bedingungen pruefen und DOM toggln.
    updateVisibility() {
        if (!this._view || !this._view.root) return;
        const visible = this._isToggleEligible();
        if (visible === this._lastVisible) return;
        this._lastVisible = visible;
        this._view.root.classList.toggle('hidden', !visible);
        // Body-Klasse, damit #translatedCaption per CSS hoeher
        // gerendert werden kann -- vermeidet Ueberlappung mit dem
        // Toggle unten links (siehe call.scss).
        document.body.classList.toggle('audio-toggle-visible', visible);

        if (visible) {
            this._maybeShowOnboarding();
        } else {
            // Tooltip ausblenden, Mode auf Default zuruecksetzen, damit
            // Speaker-Mute beim erneuten Einblenden sauber startet.
            this._dismissOnboarding(/*persist*/ false);
            VCS.state.audioFaderMode = 'original';
            this._applyMode('original');
            this._renderMode('original');
        }
    }

    onEngineInfo(msg) {
        if (!msg) return;
        VCS.state.captionEngine = msg.engine || null;
        VCS.state.captionAudioAvailable = !!msg.audioAvailable;
        // Backend schickt sourceLang -- die Sprache, die wir hoeren --
        // im 1:1 = peerLang. Damit die Sprach-Diff-Pruefung greift.
        if (msg.sourceLang) {
            VCS.state.peerLang = msg.sourceLang;
        }
        // Genau jetzt faengt der Server an, Audio schicken zu koennen -- also
        // jetzt melden, ob wir es wollen. Deckt auch den Fall ab, dass die
        // Caption-Pipeline mitten im Call neu aufgebaut wurde und der Server
        // unseren Wunsch nicht mehr kennt.
        this._lastAudioPrefSent = null;
        this._sendAudioPref((VCS.state.audioFaderMode === 'translated') && !VCS.state.speakerOff);
        this.updateVisibility();
    }

    // -------------------------------------------------------------
    // Handler fuer uebersetzten Audio-Chunk (signaling -> AudioFader)
    // -------------------------------------------------------------

    // msg = { from, senderName, sourceLang, targetLang, sampleRate,
    //         audio (b64), ts }
    handleChunk(msg) {
        if (!msg || typeof msg.audio !== 'string' || !msg.audio.length) return;

        const ctx = this._ensureContext();
        if (!ctx) return;

        let pcm;
        try {
            pcm = this._decodeBase64Pcm16(msg.audio);
        } catch (err) {
            console.warn('[audio-toggle] Dekodier-Fehler:', err && err.message);
            return;
        }
        if (!pcm.length) return;

        const sampleRate = msg.sampleRate || 24000;
        const buffer = ctx.createBuffer(1, pcm.length, sampleRate);
        buffer.copyToChannel(pcm, 0, 0);

        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(VCS.state.audioFaderTranslatedGain);

        const now = ctx.currentTime;
        let startAt = this._nextStartAt;
        if (startAt < now || (startAt - now) > this._maxQueueAheadSec) {
            startAt = now;
        }
        src.start(startAt);
        this._nextStartAt = startAt + buffer.duration;
    }

    // Tear-down bei Call-Ende / Caption-Release. Aufruf aus captions.js.
    release() {
        VCS.state.audioFaderActive = false;
        this._nextStartAt = 0;
        // Caption-Pipeline kann mitten im Call neu starten. Cache leeren,
        // damit der Wunsch danach erneut gemeldet wird.
        this._lastAudioPrefSent = null;
        try { VCS.state.audioFaderTranslatedGain?.disconnect(); } catch (_) {}
        VCS.state.audioFaderTranslatedGain = null;
        if (VCS.state.audioFaderCtx) {
            VCS.state.audioFaderCtx.close().catch(() => {});
            VCS.state.audioFaderCtx = null;
        }
        VCS.state.captionEngine = null;
        VCS.state.captionAudioAvailable = false;
        VCS.state.audioFaderMode = 'original';
        // <video>-Element zuruecksetzen (falls Speaker-Mute oder
        // Translated-Mode es noch gemutet hat).
        const remoteVid = document.getElementById('remoteVideo');
        if (remoteVid) {
            remoteVid.muted = false;
            remoteVid.volume = 1;
        }
        this._lastVisible = false;
        if (this._view && this._view.root) {
            this._view.root.classList.add('hidden');
        }
        document.body.classList.remove('audio-toggle-visible');
        this._dismissOnboarding(/*persist*/ false);
    }

    // -------------------------------------------------------------
    // Intern: AudioContext + Audio-Graph (nur Translated-Pfad)
    // -------------------------------------------------------------

    _ensureContext() {
        if (VCS.state.audioFaderCtx && VCS.state.audioFaderCtx.state !== 'closed') {
            return VCS.state.audioFaderCtx;
        }
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) {
            console.warn('[audio-toggle] AudioContext nicht unterstuetzt');
            return null;
        }
        let ctx;
        try {
            ctx = new Ctor({ sampleRate: 24000 });
        } catch (_) {
            ctx = new Ctor();
        }
        // iOS Safari startet den Context oft im 'suspended'-State und
        // braucht eine User-Geste fuer resume(). Call-Annehmen war eine
        // Geste -- versuchen. Bei Fehlschlag laeuft der Context, sobald
        // der naechste Touch (z.B. Toggle-Klick) den Resume triggert.
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const mode = VCS.state.audioFaderMode || 'original';
        const speakerOff = !!VCS.state.speakerOff;

        const translatedGain = ctx.createGain();
        translatedGain.gain.value = (mode === 'translated' && !speakerOff) ? 1 : 0;
        translatedGain.connect(ctx.destination);

        VCS.state.audioFaderCtx = ctx;
        VCS.state.audioFaderTranslatedGain = translatedGain;
        VCS.state.audioFaderActive = true;
        this._nextStartAt = 0;

        console.info('[audio-toggle] Context bereit, sampleRate=', ctx.sampleRate, 'mode=', mode);
        return ctx;
    }

    _applyMode(mode) {
        const speakerOff = !!VCS.state.speakerOff;
        const wantAudio = (mode === 'translated') && !speakerOff;
        // Translated-Gain
        if (VCS.state.audioFaderTranslatedGain) {
            VCS.state.audioFaderTranslatedGain.gain.value = wantAudio ? 1 : 0;
        }
        // <video>-Element: Mode 'original' und kein Speaker-Mute -> direkt.
        // Mode 'translated' oder Speaker-Mute -> stumm.
        const remoteVid = document.getElementById('remoteVideo');
        if (remoteVid) {
            remoteVid.muted = (mode === 'translated') || speakerOff;
        }
        // Dem Server sagen, ob das uebersetzte Audio ueberhaupt gebraucht wird.
        // Gleiche Bedingung wie der Gain oben: was hier auf 0 laufen wuerde,
        // muss gar nicht erst uebertragen werden (~500 kbit/s).
        this._sendAudioPref(wantAudio);
    }

    // Audio-Wunsch an den Server melden. Nur bei echter Aenderung, und der
    // Cache wird erst gesetzt wenn die WS wirklich offen war -- sonst
    // verschluckt ein Send waehrend Reconnect die Meldung dauerhaft.
    _sendAudioPref(want) {
        const w = !!want;
        if (this._lastAudioPrefSent === w) return;
        const ws = VCS.state.ws;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        VCS.signaling?.send({
            type     : 'caption-audio-pref',
            id       : VCS.state.myId,
            wantAudio: w,
        });
        this._lastAudioPrefSent = w;
    }

    _renderMode(mode) {
        if (!this._view || !this._view.root) return;
        const root = this._view.root;
        const isTrans = (mode === 'translated');
        root.setAttribute('aria-checked', String(isTrans));
        root.classList.toggle('is-translated', isTrans);
        const tr = (typeof t === 'function') ? t : ((k) => k);
        const ariaKey = isTrans
            ? 'call.audioToggle.value.translated'
            : 'call.audioToggle.value.original';
        root.setAttribute('aria-valuetext', tr(ariaKey));
    }

    // -------------------------------------------------------------
    // Intern: PCM-Dekodierung
    // -------------------------------------------------------------

    _decodeBase64Pcm16(b64) {
        const bin = atob(b64);
        const len = bin.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
        const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const samples = Math.floor(len / 2);
        const out = new Float32Array(samples);
        for (let i = 0; i < samples; i++) {
            const s16 = view.getInt16(i * 2, true);
            out[i] = s16 / 32768;
        }
        return out;
    }

    // -------------------------------------------------------------
    // Intern: Sichtbarkeits-Pruefung (5 Bedingungen)
    // -------------------------------------------------------------

    _isToggleEligible() {
        const s = VCS.state;
        let reason = null;

        if (!s.chatPlanLiveQuota) {
            reason = 'plan=' + s.chatMyPlan;
        } else if (s.captionEngine !== 'openai-realtime') {
            reason = 'engine=' + s.captionEngine;
        } else if (s.captionAudioAvailable !== true) {
            reason = 'audioAvail=' + s.captionAudioAvailable;
        } else {
            const inCall = s.sessionState && s.sessionState.phase === 'call';
            const inGroup = !!s.groupCallActive;
            if (!inCall && !inGroup) {
                reason = 'phase=' + (s.sessionState && s.sessionState.phase) + ' group=' + inGroup;
            } else if (inGroup) {
                // Group-Call: in Iter 1 ausgeblendet (per-Sprecher-Lang
                // noch nicht ans Frontend geroutet).
                reason = 'group-iter1-hidden';
            } else {
                const myLang = VCS.i18n ? VCS.i18n.normalizeLang(s.currentLang) : s.currentLang;
                if (!s.peerLang || s.peerLang === myLang) {
                    reason = 'peerLang=' + s.peerLang + ' myLang=' + myLang;
                }
            }
        }

        // Diagnose-Log nur bei Aenderung des Grundes (sonst Spam)
        if (reason !== this._lastEligibleReason) {
            this._lastEligibleReason = reason;
           // console.warn('[audio-toggle]', reason ? 'hidden:' : 'visible', reason || '');
        }

        return reason === null;
    }

    // -------------------------------------------------------------
    // Intern: Pointer / Tastatur
    // -------------------------------------------------------------

    _bindClick() {
        const root = this._view.root;
        if (!root) return;
        // Klick toggelt; pointerup statt click vermeidet 300ms-Delay
        // auf alten Mobile-Safaris.
        root.addEventListener('click', (e) => {
            if (root.classList.contains('hidden')) return;
            this.toggle();
            e.preventDefault();
        });
    }

    _bindKeyboard() {
        const root = this._view.root;
        if (!root) return;
        root.addEventListener('keydown', (e) => {
            if (root.classList.contains('hidden')) return;
            switch (e.key) {
                case ' ':
                case 'Enter':
                case 'ArrowLeft':
                case 'ArrowRight':
                    e.preventDefault();
                    this.toggle();
                    break;
                case 'Home':
                    e.preventDefault();
                    this.setMode('original');
                    break;
                case 'End':
                    e.preventDefault();
                    this.setMode('translated');
                    break;
            }
        });
    }

    // -------------------------------------------------------------
    // Intern: Onboarding-Tooltip (1x pro Geraet)
    // -------------------------------------------------------------

    _maybeShowOnboarding() {
        if (!this._view || !this._view.onboarding) return;
        try {
            if (localStorage.getItem('audioToggleTooltipSeen') === '1') return;
        } catch (_) {}
        this._view.onboarding.classList.remove('hidden');
        if (this._onboardingTimer) clearTimeout(this._onboardingTimer);
        this._onboardingTimer = setTimeout(() => this._dismissOnboarding(true), 6000);
    }

    _dismissOnboarding(persist) {
        if (!this._view || !this._view.onboarding) return;
        this._view.onboarding.classList.add('hidden');
        if (this._onboardingTimer) {
            clearTimeout(this._onboardingTimer);
            this._onboardingTimer = null;
        }
        if (persist) {
            try { localStorage.setItem('audioToggleTooltipSeen', '1'); } catch (_) {}
        }
    }
};

// Singleton, beim Bundle-Init angehaengt (analog VCS.captions etc.).
VCS.audioFader = new VCS.AudioFader();
